import React, { useState, useRef, useEffect } from 'react';
import { TESTIMONIALS } from '../data/simatechData';
import { useCms } from '../context/CmsContext';


export const TestimonialsSection: React.FC = () => {
  const { siteData } = useCms();
  const homeData = siteData?.pages?.home;
  const sectionBgColor = homeData?.testimonialsSectionBgColor || '#ffffff';
  const sectionBadge = homeData?.testimonialsBadge || homeData?.testimonialsSectionBadge || '';
  const sectionHeading = homeData?.testimonialsHeading || homeData?.testimonialsSectionHeading || 'Feedback from Our Valued Industrial Clients';
  const sectionSubheading = homeData?.testimonialsSubheading || homeData?.testimonialsSectionSubheading || '';

  // Dynamic typography overrides
  const headingFontFamily = homeData?.testimonialsHeadingFontFamily || "'Jost', sans-serif";
  const headingFontSize = homeData?.testimonialsHeadingFontSize;
  const headingFontWeight = homeData?.testimonialsHeadingFontWeight || '300';
  const headingColor = homeData?.testimonialsHeadingColor;

  const subheadingFontFamily = homeData?.testimonialsSubheadingFontFamily || 'monospace';
  const subheadingFontSize = homeData?.testimonialsSubheadingFontSize;
  const subheadingColor = homeData?.testimonialsSubheadingColor;

  const quoteFontFamily = homeData?.testimonialsQuoteFontFamily || "'Google Sans Flex', sans-serif";
  const quoteFontSize = homeData?.testimonialsQuoteFontSize;
  const quoteColor = homeData?.testimonialsQuoteColor || '#334155';

  const authorFontFamily = homeData?.testimonialsAuthorFontFamily || "'Jost', sans-serif";
  const authorFontSize = homeData?.testimonialsAuthorFontSize;
  const authorColor = homeData?.testimonialsAuthorColor || '#06242a';

  // Use CMS testimonials if provided
  const cmsTestimonials = homeData?.testimonials;
  const rawTestimonials = cmsTestimonials && cmsTestimonials.length > 0
    ? cmsTestimonials.map((t: any, i: number) => {
        const authorName = t.author || t.name || t.clientName || 'Valued Client';
        return {
          id: `cms-t-${i}`,
          quote: t.content || t.quote || '',
          clientName: authorName,
          company: t.company || '',
          role: t.role || '',
          location: t.location || '',
          avatar: t.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(authorName)}&background=1b7481&color=fff&size=128`,
        };
      })
    : TESTIMONIALS;

  const sliderRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Luminance calculation to guarantee high text contrast against dark or light background
  const isDarkBg = (() => {
    if (!sectionBgColor || !sectionBgColor.startsWith('#')) return true;
    const hex = sectionBgColor.replace('#', '');
    const r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.substring(0, 2), 16);
    const g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.substring(2, 4), 16);
    const b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 140;
  })();

  // Duplicate list to create seamless infinite loop effect
  const tripleTestimonials = [...rawTestimonials, ...rawTestimonials, ...rawTestimonials];

  // Auto-scroll loop
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    let animationFrameId: number;
    const speed = 0.65; // Smooth continuous scroll speed

    const step = () => {
      if (!isDragging && !isHovered && slider) {
        slider.scrollLeft += speed;

        // Reset scroll seamlessly when reaching 2/3 of content
        const maxScroll = slider.scrollWidth / 3;
        if (slider.scrollLeft >= maxScroll * 2) {
          slider.scrollLeft -= maxScroll;
        }
      }
      animationFrameId = requestAnimationFrame(step);
    };

    animationFrameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isDragging, isHovered]);

  // Initial center scroll position
  useEffect(() => {
    if (sliderRef.current) {
      const slider = sliderRef.current;
      slider.scrollLeft = slider.scrollWidth / 3;
    }
  }, []);

  // Mouse Drag Handlers (Desktop)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!sliderRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Drag sensitivity
    sliderRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUpOrLeave = () => {
    setIsDragging(false);
  };

  // Touch Swipe Handlers (Mobile)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!sliderRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !sliderRef.current) return;
    const x = e.touches[0].pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    sliderRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="py-16 sm:py-20 lg:py-24 overflow-hidden border-t border-[#1b6f7e]/15 relative"
      style={{ backgroundColor: sectionBgColor }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* ========================================================================= */}
        {/* 1. CENTERED SECTION HEADER                                                */}
        {/* ========================================================================= */}
        <div
          className={`flex flex-col items-center text-center mb-10 sm:mb-12 lg:mb-14 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {sectionBadge && (
            <span
              className="text-xs uppercase tracking-[0.25em] font-semibold mb-3 sm:mb-3.5"
              style={{
                fontFamily: subheadingFontFamily,
                fontSize: subheadingFontSize || undefined,
                color: subheadingColor || (isDarkBg ? '#38c9de' : '#1b7481'),
              }}
            >
              {sectionBadge}
            </span>
          )}

          <h2
            className="text-[22px] sm:text-[27px] md:text-[36px] font-light tracking-tight leading-[1.25]"
            style={{
              fontFamily: headingFontFamily,
              fontSize: headingFontSize || undefined,
              fontWeight: headingFontWeight,
              color: headingColor || (isDarkBg ? '#ffffff' : '#06242a'),
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: sectionHeading }} />
          </h2>
          <div className={`h-[1px] w-16 mx-auto mt-3.5 bg-gradient-to-r ${
            isDarkBg ? 'from-[#38c9de] to-[#1b7481]' : 'from-[#1b7481] to-[#38c9de]'
          }`} />

          {sectionSubheading && (
            <p
              className="mt-3.5 text-sm sm:text-[15px] font-light max-w-2xl mx-auto leading-relaxed"
              style={{
                fontFamily: quoteFontFamily,
                color: isDarkBg ? '#94a3b8' : '#475569',
              }}
              dangerouslySetInnerHTML={{ __html: sectionSubheading }}
            />
          )}
        </div>

        {/* ========================================================================= */}
        {/* 2. DRAGGABLE & AUTO-SCROLLING STRAIGHT SQUARE CAROUSEL TRACK (NO CURVES)  */}
        {/* ========================================================================= */}
        <div
          ref={sliderRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUpOrLeave}
          onMouseLeave={() => {
            handleMouseUpOrLeave();
            setIsHovered(false);
          }}
          onMouseEnter={() => setIsHovered(true)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className={`flex gap-6 sm:gap-7 overflow-x-auto no-scrollbar py-2 sm:py-3 select-none transition-all duration-700 delay-200 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          } ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          style={{
            scrollBehavior: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {tripleTestimonials.map((item, idx) => (
            <div
              key={`${item.id}-${idx}`}
              className="w-[300px] sm:w-[360px] md:w-[400px] shrink-0 bg-white rounded-none border border-[#06242a]/10 hover:border-slate-300 p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_45px_rgba(6,36,42,0.12)] hover:z-20 relative group"
            >
              {/* Top Subtle Hover Accent Bar (Straight Line) */}
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div>
                {/* Dual-Tone Large Quotation Vector Icon */}
                <div className="mb-4 sm:mb-5">
                  <svg
                    viewBox="0 0 48 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-9 h-7 sm:w-11 sm:h-8"
                  >
                    <path
                      d="M0 24C0 14.5 6.5 7.5 17 4L19.5 8C12.5 10.5 10 14.5 9.5 18.5H19V38H0V24ZM26 24C26 14.5 32.5 7.5 43 4L45.5 8C38.5 10.5 36 14.5 35.5 18.5H45V38H26V24Z"
                      fill="url(#quote-gradient-straight)"
                    />
                    <defs>
                      <linearGradient id="quote-gradient-straight" x1="0" y1="4" x2="45" y2="38" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#1b7481" />
                        <stop offset="1" stopColor="#38c9de" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                {/* Quote Text */}
                <p
                  className="text-[15px] font-light leading-relaxed mb-6"
                  style={{
                    fontFamily: quoteFontFamily,
                    fontSize: quoteFontSize || undefined,
                    color: quoteColor,
                  }}
                >
                  "{item.quote}"
                </p>
              </div>

              {/* Straight Divider Line & Client Profile */}
              <div className="pt-5 border-t border-slate-100 flex items-center gap-3.5">
                <img
                  src={item.avatar}
                  alt={item.clientName}
                  className="w-12 h-12 rounded-full object-cover shrink-0"
                  referrerPolicy="no-referrer"
                  loading="lazy"
                  decoding="async"
                  width={48}
                  height={48}
                  draggable={false}
                />

                <div className="min-w-0">
                  <div
                    className="text-base font-medium tracking-tight truncate"
                    style={{
                      fontFamily: authorFontFamily,
                      fontSize: authorFontSize || undefined,
                      color: authorColor,
                    }}
                  >
                    {item.clientName}
                  </div>
                  <div className="text-xs text-slate-400 font-light truncate">
                    {item.role ? `${item.role} • ` : ''}{item.company}{item.location ? ` (${item.location})` : ''}
                  </div>
                </div>
              </div>

            </div>
          ))}
        </div>

        {/* Drag Hint Footer */}
        <div className={`flex items-center justify-center text-[11px] font-mono uppercase tracking-widest -mt-6 sm:-mt-8 select-none ${
          isDarkBg ? 'text-[#38c9de]/85' : 'text-[#1b7481]/75'
        }`}>
          <span>&larr; Drag or hover to explore reviews &rarr;</span>
        </div>

      </div>
    </section>
  );
};
